import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Upload } from 'lucide-react';

/**
 * The ONLY way pages should render a primary or secondary quote CTA.
 *
 * Phase 8 refinement: refined, editorial button styling — less app-y,
 * less drop-shadow heavy, slightly tighter radius, more horizontal
 * breathing room. CTA props/routes unchanged.
 *
 * Hard contract:
 *  - Primary CTA copy: "Get My Price" (or short variant)
 *  - Secondary CTA copy: "See How It Works"
 *  - BOTH always route to /get-quote with an optional ?src= tag.
 *  - Never to /quote (the gated intake) directly.
 */

const trackCtaClick = (src, variant) => {
  if (typeof window === 'undefined') return;
  try {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'conversion',
        event_label: src,
        cta_variant: variant,
      });
    }
    if (window.fbq) {
      window.fbq('trackCustom', 'CTAClick', { src, variant });
    }
  } catch (_) {
    /* never let analytics break a click */
  }
};

const SRC_PARAM = (src) => (src ? `?src=${encodeURIComponent(src)}` : '');

export function PrimaryCTA({
  src = 'homepage',
  label = 'Get My Price',
  shortLabel,
  icon: Icon = Upload,
  size = 'lg',
  className = '',
  fullWidthMobile = true,
  testId = 'cta-get-my-price',
}) {
  return (
    <Link
      to={`/get-quote${SRC_PARAM(src)}`}
      onClick={() => trackCtaClick(src, 'primary')}
      className="inline-block"
      data-testid={testId}
    >
      <Button
        size={size}
        className={`bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg h-12 sm:h-[52px] px-7 sm:px-9 text-[15px] sm:text-base font-medium tracking-tight transition-all hover:opacity-[0.96] hover:shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
          fullWidthMobile ? 'w-full sm:w-auto' : ''
        } ${className}`}
      >
        {Icon ? <Icon className="mr-2.5 h-4 w-4" strokeWidth={1.75} /> : null}
        <span className={shortLabel ? 'hidden sm:inline' : ''}>{label}</span>
        {shortLabel ? <span className="sm:hidden">{shortLabel}</span> : null}
        <ArrowRight className="ml-2.5 h-4 w-4" strokeWidth={1.75} />
      </Button>
    </Link>
  );
}

export function SecondaryCTA({
  src = 'homepage',
  label = 'See How It Works',
  to = '/get-quote',
  testId = 'cta-see-how-it-works',
  className = '',
}) {
  // Hard guarantee: secondary CTAs from marketing pages never bypass the gate.
  const safeTo = to === '/quote' ? '/get-quote' : to;
  return (
    <Link
      to={`${safeTo}${SRC_PARAM(src)}`}
      onClick={() => trackCtaClick(src, 'secondary')}
      className="inline-block"
      data-testid={testId}
    >
      <Button
        variant="ghost"
        size="lg"
        className={`rounded-lg h-12 sm:h-[52px] px-6 sm:px-7 text-[15px] sm:text-base font-medium tracking-tight text-[var(--snap-charcoal)] hover:bg-[var(--snap-stone-100)] hover:text-[var(--snap-charcoal)] ${className}`}
      >
        {label}
        <span
          className="ml-2 text-[var(--snap-champagne-600)] text-lg leading-none"
          aria-hidden="true"
        >
          ›
        </span>
      </Button>
    </Link>
  );
}

export default PrimaryCTA;
